import type { QueryClient } from "@tanstack/react-query";

import type {
  ListNotificationsResponse,
  NotificationFilter,
  NotificationInboxItem,
  NotificationSummary,
} from "@recobra/schemas/notification";

import { notificationsQueryKey } from "./useNotifications";
import { notificationSummaryQueryKey } from "./useNotificationSummary";

export type NotificationQuerySnapshot = {
  lists: Array<readonly [ReturnType<typeof notificationsQueryKey>, ListNotificationsResponse]>;
  summary: NotificationSummary | undefined;
};

const notificationFilters = [
  "all",
  "unread",
  "mention",
  "system",
] as const satisfies readonly NotificationFilter[];

export const notificationsRootKey = ["notifications"] as const;

export const listFilter = (
  queryKey: ReturnType<typeof notificationsQueryKey>,
): NotificationFilter => queryKey[1];

const markItemRead = (item: NotificationInboxItem, readAt: string): NotificationInboxItem => ({
  ...item,
  isUnread: false,
  readAt,
});

export const patchListForMarkRead = (
  data: ListNotificationsResponse,
  id: string,
  filter: NotificationFilter,
  readAt: string,
): ListNotificationsResponse => ({
  items: data.items
    .map((item) => (item.id === id ? markItemRead(item, readAt) : item))
    .filter((item) => filter !== "unread" || item.isUnread),
});

export const patchListForMarkAllRead = (
  data: ListNotificationsResponse,
  filter: NotificationFilter,
  readAt: string,
): ListNotificationsResponse => ({
  items: data.items
    .map((item) => markItemRead(item, readAt))
    .filter((item) => filter !== "unread" || item.isUnread),
});

export const findNotificationItem = (
  lists: NotificationQuerySnapshot["lists"],
  id: string,
): NotificationInboxItem | undefined => {
  for (const [, data] of lists) {
    const item = data.items.find((candidate) => candidate.id === id);
    if (item) return item;
  }

  return undefined;
};

export const patchSummaryForMarkRead = (
  summary: NotificationSummary,
  item: NotificationInboxItem,
): NotificationSummary => {
  if (!item.isUnread) return summary;

  const byCategory = { ...summary.byCategory };
  if (item.category === "mention") {
    byCategory.mention = Math.max(0, byCategory.mention - 1);
  }
  if (item.category === "system") {
    byCategory.system = Math.max(0, byCategory.system - 1);
  }

  return {
    ...summary,
    unread: Math.max(0, summary.unread - 1),
    read: summary.read + 1,
    byCategory,
  };
};

export const snapshotNotificationQueries = (
  queryClient: QueryClient,
): NotificationQuerySnapshot => {
  const lists = notificationFilters.flatMap((filter) => {
    const queryKey = notificationsQueryKey(filter);
    const data = queryClient.getQueryData<ListNotificationsResponse>(queryKey);
    if (!data) return [];

    return [[queryKey, data] as const];
  });

  return {
    lists,
    summary: queryClient.getQueryData<NotificationSummary>(notificationSummaryQueryKey),
  };
};

export const restoreNotificationQueries = (
  queryClient: QueryClient,
  snapshot: NotificationQuerySnapshot | undefined,
) => {
  if (!snapshot) return;

  for (const [queryKey, data] of snapshot.lists) {
    queryClient.setQueryData(queryKey, data);
  }

  if (snapshot.summary) {
    queryClient.setQueryData(notificationSummaryQueryKey, snapshot.summary);
  }
};
